import { CYRC_RUNWAYS, CYRCRunway } from './runwayData';

export interface Point {
  x: number;
  y: number;
}

export interface AirportBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  latRef: number;
  lonRef: number;
}

export interface RunwaySvgGeometry {
  id: string;
  runwayA: string;
  runwayB: string;
  lengthM: number;
  widthM: number;
  lighted: boolean;
  closed: boolean;
  ptA: Point;
  ptB: Point;
  svgWidth: number; // visual stroke width in SVG units
}

/**
 * Compute reference lat/lon (centroid of all thresholds) and
 * min/max bounds in meters.
 */
export function getAirportBounds(runways: CYRCRunway[] = CYRC_RUNWAYS): AirportBounds {
  const thresholds = runways.flatMap((r) => [r.thresholdA, r.thresholdB]);
  const latRef = thresholds.reduce((s, t) => s + t.lat, 0) / thresholds.length;
  const lonRef = thresholds.reduce((s, t) => s + t.lon, 0) / thresholds.length;

  const pts = thresholds.map((t) => projectLatLonToMeters(t.lat, t.lon, latRef, lonRef));
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
    latRef,
    lonRef,
  };
}

/**
 * Convert a lat/lon to local meters relative to a reference point.
 * x = east (positive = east), y = north (positive = north)
 */
export function projectLatLonToMeters(
  lat: number,
  lon: number,
  latRef: number,
  lonRef: number
): Point {
  const DEG_TO_RAD = Math.PI / 180;
  const x = (lon - lonRef) * Math.cos(latRef * DEG_TO_RAD) * 111320;
  const y = (lat - latRef) * 110540;
  return { x, y };
}

/**
 * Normalize a meter-space point into SVG pixel coordinates.
 * SVG convention: x right, y down → we flip y so north is up.
 */
export function normalizeToSvg(
  pt: Point,
  bounds: AirportBounds,
  svgW: number,
  svgH: number,
  margin: number
): Point {
  const rangeX = bounds.maxX - bounds.minX || 1;
  const rangeY = bounds.maxY - bounds.minY || 1;

  const drawW = svgW - 2 * margin;
  const drawH = svgH - 2 * margin;

  // Uniform scale to preserve aspect ratio
  const scale = Math.min(drawW / rangeX, drawH / rangeY);

  // Center the diagram
  const offsetX = margin + (drawW - rangeX * scale) / 2;
  const offsetY = margin + (drawH - rangeY * scale) / 2;

  const svgX = offsetX + (pt.x - bounds.minX) * scale;
  // Flip y: SVG y increases downward, but north should be up
  const svgY = offsetY + (bounds.maxY - pt.y) * scale;

  return { x: svgX, y: svgY };
}

/**
 * Build SVG geometry for all runways from real coordinates.
 * svgW / svgH: target SVG dimensions
 * margin: padding around the diagram
 */
export function buildRunwaySvgGeometry(
  svgW: number,
  svgH: number,
  margin: number,
  runways: CYRCRunway[] = CYRC_RUNWAYS
): RunwaySvgGeometry[] {
  const bounds = getAirportBounds(runways);

  // Compute the pixel-per-meter scale to derive visual runway width
  const rangeX = bounds.maxX - bounds.minX || 1;
  const rangeY = bounds.maxY - bounds.minY || 1;
  const drawW = svgW - 2 * margin;
  const drawH = svgH - 2 * margin;
  const scale = Math.min(drawW / rangeX, drawH / rangeY);

  return runways.map((rwy) => {
    const mA = projectLatLonToMeters(rwy.thresholdA.lat, rwy.thresholdA.lon, bounds.latRef, bounds.lonRef);
    const mB = projectLatLonToMeters(rwy.thresholdB.lat, rwy.thresholdB.lon, bounds.latRef, bounds.lonRef);

    const ptA = normalizeToSvg(mA, bounds, svgW, svgH, margin);
    const ptB = normalizeToSvg(mB, bounds, svgW, svgH, margin);

    // Visual width: real width in meters × scale, clamped to readable range
    const visualWidth = Math.max(4, Math.min(14, rwy.widthM * scale));

    return {
      id: rwy.id,
      runwayA: rwy.runwayA,
      runwayB: rwy.runwayB,
      lengthM: rwy.lengthM,
      widthM: rwy.widthM,
      lighted: rwy.lighted,
      closed: rwy.closed,
      ptA,
      ptB,
      svgWidth: visualWidth,
    };
  });
}
